import React, { useState, useEffect, useMemo } from 'react';
import {
  Home,
  Users,
  User,
  Building2,
  FolderKanban,
  Settings,
  ChevronDown,
  List,
  Eye,
  Phone,
  Calendar,
  ClipboardCheck,
  UserCheck,
  FileText,
  CheckCircle,
  Share2,
  BarChart2,
  Star,
  Clock,
  Zap,
  GraduationCap,
  Puzzle,
  Contact,
  Inbox,
  BookOpen,
  ClipboardList,
  ListChecks,
  Lightbulb,
  type LucideIcon,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import client from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import { useAppStore } from '../../store/appStore';
import { UserRole } from '../../types';

/**
 * Map icon name strings (from the database / API) to lucide-react components.
 *
 * Anything not listed here falls back to `Users` (see the lookup below), which
 * is why an unmapped name renders the same "two people" glyph as Talent
 * Acquisition rather than an obvious error. Add the key here *and* the import
 * above whenever a migration seeds a new icon name.
 */
const iconMap: Record<string, LucideIcon> = {
  home: Home,
  users: Users,
  user: User,
  building2: Building2,
  'folder-kanban': FolderKanban,
  settings: Settings,
  list: List,
  eye: Eye,
  phone: Phone,
  calendar: Calendar,
  'clipboard-check': ClipboardCheck,
  'user-check': UserCheck,
  'file-text': FileText,
  'check-circle': CheckCircle,
  'share-2': Share2,
  'bar-chart-2': BarChart2,
  star: Star,
  clock: Clock,
  zap: Zap,
  // Skills Management (WI #319). `graduation-cap`, `puzzle` and `contact` are
  // seeded by migration 036; the remaining three have been in the database
  // since migrations 029/035 and were rendering the fallback glyph.
  'graduation-cap': GraduationCap,
  puzzle: Puzzle,
  contact: Contact,
  inbox: Inbox,
  'book-open': BookOpen,
  'list-checks': ListChecks,
  // My Assessments, seeded by migration 038 (WI #325).
  'clipboard-list': ClipboardList,
  // Skill Requests, seeded by migration 044 (WI #374).
  lightbulb: Lightbulb,
};

interface NavItem {
  id: string;
  label: string;
  path?: string;
  icon: React.ElementType;
  children?: NavItem[];
  requiresAdmin?: boolean;
}

interface NavMenuProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

// Split an item's `path` (which may include a query string, e.g.
// "/tap/applications?stage=PhoneScreen") into a pathname + search pair so
// react-router-dom's navigate() can route correctly. Previously we assigned
// to window.location.pathname directly, which URL-encoded the "?" into "%3F"
// and produced a dead route like "/tap/applications%3Fstage=PhoneScreen".
const splitPath = (path: string): { pathname: string; search: string } => {
  const qIdx = path.indexOf('?');
  if (qIdx === -1) return { pathname: path, search: '' };
  return {
    pathname: path.slice(0, qIdx),
    search: path.slice(qIdx), // keeps the leading "?"
  };
};

// Compare the browser's current location against a nav item's path.
// An item is "active" when both its pathname and (meaningful) query params
// match — e.g. "/tap/applications?stage=Review" is active only when the URL
// has stage=Review, not for the bare "/tap/applications" page.
const locationMatchesPath = (
  currentPathname: string,
  currentSearch: string,
  itemPath: string | undefined,
): boolean => {
  if (!itemPath) return false;
  const { pathname, search } = splitPath(itemPath);
  if (pathname !== currentPathname) return false;
  if (!search) return true; // item has no query string — path-only match is enough

  const targetParams = new URLSearchParams(search);
  const currentParams = new URLSearchParams(currentSearch);
  for (const [k, v] of targetParams.entries()) {
    if (currentParams.get(k) !== v) return false;
  }
  return true;
};

// The platform-core entries that exist regardless of what /navigation
// returns. Module entries (TAP, SM) come only from the API.
const BASE_NAV_ITEMS: NavItem[] = [
  {
    id: 'home',
    label: 'Home',
    path: '/',
    icon: Home,
  },
  {
    id: 'people',
    label: 'People',
    icon: Users,
    children: [
      {
        id: 'users',
        label: 'Users',
        path: '/users',
        icon: User,
      },
    ],
  },
  {
    id: 'companies',
    label: 'Companies',
    path: '/companies',
    icon: Building2,
  },
  {
    id: 'projects',
    label: 'Projects',
    path: '/projects',
    icon: FolderKanban,
  },
  {
    id: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: Settings,
    requiresAdmin: true,
  },
];

export const NavMenu: React.FC<NavMenuProps> = ({ isCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [navItems, setNavItems] = useState<NavItem[]>(BASE_NAV_ITEMS);
  // Feature #385: Settings › Apps bumps this after a module toggle so the
  // menu refetches and a switched-off module's rows disappear immediately.
  const navVersion = useAppStore((s) => s.navVersion);

  useEffect(() => {
    // Load dynamic nav items from API via the authenticated axios client
    const fetchNavItems = async () => {
      try {
        const response = await client.get('/navigation');
        const data = response.data as { items?: any[] } | any[];
        const rawItems: any[] = Array.isArray(data) ? data : (data.items || []);
        if (rawItems.length > 0) {
          // Build a tree from the flat list returned by the API.
          // Each API item has { id, label, path, icon (string), parent_id, sort_order, ... }.
          const itemById = new Map<string, NavItem>();
          const roots: NavItem[] = [];

          // First pass — create NavItem objects
          for (const raw of rawItems) {
            const iconComponent = iconMap[(raw.icon || '').toLowerCase()] || Users;
            itemById.set(raw.id, {
              id: raw.id,
              label: raw.label,
              path: raw.path || undefined,
              icon: iconComponent,
              children: [],
            });
          }

          // Second pass — nest children under parents.
          //
          // Bug #315: an item whose parent_id pointed at something absent
          // from the payload matched neither branch and vanished without a
          // trace. That is how one missing row erased the entire Talent
          // Acquisition menu with a clean console. /navigation no longer
          // paginates so a parent should always be present — but "should"
          // is exactly what the previous version assumed. Promote the
          // orphan and say so: an entry in the wrong place is recoverable,
          // a silently missing module is not.
          for (const raw of rawItems) {
            const node = itemById.get(raw.id)!;
            if (raw.parent_id && itemById.has(raw.parent_id)) {
              itemById.get(raw.parent_id)!.children!.push(node);
            } else if (!raw.parent_id) {
              roots.push(node);
            } else {
              console.warn(
                `Nav item "${raw.label}" (${raw.id}) references parent ${raw.parent_id}, ` +
                  'which was not returned by /navigation. Rendering it at the top level.'
              );
              roots.push(node);
            }
          }

          // Clean up empty children arrays so hasChildren check works correctly
          for (const item of itemById.values()) {
            if (item.children && item.children.length === 0) {
              delete item.children;
            }
          }

          // Rebuild from the static base rather than appending to the
          // previous state: this effect now re-runs on navVersion (#385),
          // and appending would duplicate every API row on each refetch.
          setNavItems([...BASE_NAV_ITEMS, ...roots]);
        } else {
          setNavItems(BASE_NAV_ITEMS);
        }
      } catch (error) {
        console.error('Failed to load navigation items:', error);
      }
    };

    fetchNavItems();
  }, [navVersion]);

  // Derive which item (if any) is "active" and auto-expand any ancestor so
  // the user sees their current location highlighted when they navigate or
  // reload a deep link. Without this, the tree collapses on every route
  // change, producing a jarring "why did my menu just fold up?" UX.
  // Bug #198: prefer the most-specific match so that e.g. "Review"
  // (/tap/applications?stage=Review) highlights instead of "All Applications"
  // (/tap/applications) when a query-string filter is active.
  const activeItemId = useMemo(() => {
    let bestId: string | null = null;
    let bestSpecificity = -1;
    const walk = (items: NavItem[]) => {
      for (const item of items) {
        if (locationMatchesPath(location.pathname, location.search, item.path)) {
          // Specificity = number of query params in the item's path.
          const { search } = splitPath(item.path || '');
          const specificity = search ? new URLSearchParams(search).size : 0;
          if (specificity > bestSpecificity) {
            bestSpecificity = specificity;
            bestId = item.id;
          }
        }
        if (item.children) walk(item.children);
      }
    };
    walk(navItems);
    return bestId;
  }, [navItems, location.pathname, location.search]);

  // Compute parents-of-active so they get auto-expanded.
  const ancestorsOfActive = useMemo(() => {
    if (!activeItemId) return new Set<string>();
    const parents = new Set<string>();
    const walk = (items: NavItem[], trail: string[]): boolean => {
      for (const item of items) {
        if (item.id === activeItemId) {
          trail.forEach((id) => parents.add(id));
          return true;
        }
        if (item.children) {
          if (walk(item.children, [...trail, item.id])) return true;
        }
      }
      return false;
    };
    walk(navItems, []);
    return parents;
  }, [navItems, activeItemId]);

  // Merge auto-expanded ancestors with user-toggled expansion state so deep
  // links land with the correct section already open.
  useEffect(() => {
    if (ancestorsOfActive.size === 0) return;
    setExpandedItems((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of ancestorsOfActive) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [ancestorsOfActive]);

  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'var(--color-white)',
    borderRight: '1px solid var(--color-light-grey)',
    overflow: 'hidden',
  };

  const navStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '12px 0',
  };

  const renderNavItem = (item: NavItem, depth: number = 0) => {
    const IconComponent = item.icon;
    const isItemActive = item.id === activeItemId;
    const hasChildren = !!item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);

    // Active highlight should be full-width and visually aligned at every
    // depth. We achieve this with a consistent left padding (border-left
    // removed to avoid the 4px shift children used to show), and content
    // indent via `paddingLeft` only.
    const itemStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      padding: '10px 12px',
      paddingLeft: `${12 + depth * 16}px`,
      backgroundColor: isItemActive ? 'var(--color-magenta)' : 'transparent',
      color: isItemActive ? 'var(--color-white)' : 'var(--color-black)',
      cursor: 'pointer',
      transition: 'background-color 0.15s ease-in-out, color 0.15s ease-in-out',
      fontSize: '14px',
      fontFamily: 'var(--font-family)',
      fontWeight: isItemActive ? 600 : 400,
      gap: '12px',
      userSelect: 'none',
      border: 'none',
      textAlign: 'left',
    };

    const handleClick = (e: React.MouseEvent) => {
      // A parent row with children opens/closes the sub-tree. A parent row
      // that also carries its own `path` (e.g. "Talent Acquisition" → the
      // /tap/applications landing page) still primarily toggles expansion so
      // the user can reach the children — clicking the row shouldn't
      // side-effect them into a page they weren't aiming for.
      if (hasChildren) {
        e.preventDefault();
        toggleExpanded(item.id);
        return;
      }
      if (item.path) {
        e.preventDefault();
        // Use react-router navigate() so the SPA handles the route. This
        // preserves in-memory state (expanded nav, scroll position, etc.)
        // and correctly parses the query string — assigning to
        // window.location.pathname was URL-encoding "?" into "%3F".
        const { pathname, search } = splitPath(item.path);
        navigate({ pathname, search });
      }
    };

    return (
      <div key={item.id}>
        <button
          style={itemStyle}
          onClick={handleClick}
          onMouseEnter={(e) => {
            if (!isItemActive) {
              // Use a soft tint of the brand magenta for hover so it reads
              // clearly against the white sidebar — the old light-grey wash
              // was nearly invisible on most monitors.
              (e.currentTarget as HTMLElement).style.backgroundColor =
                'rgba(234, 21, 122, 0.08)';
              (e.currentTarget as HTMLElement).style.color =
                'var(--color-magenta)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isItemActive) {
              (e.currentTarget as HTMLElement).style.backgroundColor =
                'transparent';
              (e.currentTarget as HTMLElement).style.color =
                'var(--color-black)';
            }
          }}
          aria-current={isItemActive ? 'page' : undefined}
          aria-label={item.label}
        >
          <IconComponent size={20} style={{ flexShrink: 0 }} />
          {!isCollapsed && (
            <>
              <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
              {hasChildren && (
                <ChevronDown
                  size={16}
                  style={{
                    transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.2s ease-in-out',
                  }}
                />
              )}
            </>
          )}
        </button>
        {hasChildren && isExpanded && !isCollapsed && (
          <div>
            {item.children?.map((child) => renderNavItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Bug #367: `requiresAdmin` was declared on the hard-coded Settings item but
  // never read, so every user saw Settings and AdminRoute bounced the click
  // back to Home. Rows from /navigation are already role-filtered server-side;
  // this only covers the hard-coded items. Hidden while roles load, so a
  // non-admin never sees it flash in.
  const isPlatformAdmin = !!user?.roles?.includes(UserRole.PlatformAdmin);
  const visibleItems = navItems.filter((item) => !item.requiresAdmin || isPlatformAdmin);

  return (
    <nav style={containerStyle}>
      <div style={navStyle}>
        {visibleItems.map((item) => renderNavItem(item))}
      </div>
    </nav>
  );
};
